import { type File, type Memo, type MemoClassification } from '@kalos/kalos-rpc';
import { cn, Dialog, DialogContent, DialogHeader, DialogTitle } from '@kalos/ui';
import { format, parseISO } from 'date-fns/esm';
import { type ReactNode } from 'react';

import { OmniGallery } from '../../OmniGallery';

const NOT_SET = 'Not set';

const Fact = ({
  label,
  value,
  wide = false,
}: {
  label: string;
  value: ReactNode;
  wide?: boolean;
}) => (
  <div className={wide ? 'col-span-2' : undefined}>
    <span className="text-muted-foreground">{label}</span>{' '}
    <span className="text-foreground">{value}</span>
  </div>
);

/** An AI report's photos in the gallery, each with the note it came from. */
export const ReportPhotoGalleryDialog = ({
  open,
  onOpenChange,
  photoFiles,
  fileToMemoMap,
  categories,
  className,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  photoFiles: File[];
  fileToMemoMap: Map<number, Memo>;
  categories?: MemoClassification[];
  /** Extra classes for the dialog, such as its surface. */
  className?: string;
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent
      className={cn(
        'bg-card flex max-h-[90vh] max-w-screen-lg flex-col gap-2 p-2 pt-10 sm:gap-4 sm:p-4 sm:pt-4',
        className,
      )}
    >
      <DialogHeader className="mr-10">
        <DialogTitle>
          Photos ({photoFiles.length} {photoFiles.length === 1 ? 'photo' : 'photos'})
        </DialogTitle>
      </DialogHeader>
      <div className="flex h-[calc(90vh-120px)] w-full">
        {photoFiles.length > 0 && (
          <OmniGallery
            files={photoFiles}
            title="Photos from the report's notes"
            showVideos={false}
            readOnly={true}
            renderPhotoHeader={(file) => {
              const memo = fileToMemoMap.get(file.id ?? 0);
              if (!memo) return null;
              return (
                <div className="px-4 py-3">
                  <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                    <Fact label="Job number" value={memo.eventId || NOT_SET} />
                    <Fact
                      label="Added"
                      value={
                        memo.createdDate
                          ? format(parseISO(memo.createdDate), 'MM/dd/yyyy, h:mm a')
                          : NOT_SET
                      }
                    />
                    <Fact label="Property" value={memo.propertyAddressSearch || NOT_SET} wide />
                    <Fact label="Department" value={memo.userDepartmentName || NOT_SET} />
                    <Fact label="Added by" value={memo.createdByName || NOT_SET} />
                    <Fact
                      label="Category"
                      value={
                        categories?.find((c) => c.id === memo.classificationId)
                          ?.categoryDescription || NOT_SET
                      }
                    />
                    <Fact label="Assigned to" value={memo.assigneeName || 'Unassigned'} />
                  </div>
                </div>
              );
            }}
          />
        )}
      </div>
    </DialogContent>
  </Dialog>
);
